import { useEffect, useState } from 'react';
import { Flame, Swords, Trophy, Mic } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2N PR 3 — Live Commentary Overlays.
 *
 * Composes 4 emotional types of live commentary into a top overlay that
 * floats above the spectator-room body:
 *
 *  - momentum  (🔥 — "Strikers are pushing hard right now")
 *  - rivalry   (⚔️ — "This fixture somehow gets louder every meeting")
 *  - champ     (🏆 — "One goal away from defending the title")
 *  - hot_mic   (🎙 — short captain pin, expires after 12s)
 *
 * Pulls the source-of-truth commentary timeline from the room and detects
 * which kind to surface. Hot-mic takes top priority; other types rotate.
 *
 * Auto-dismisses after a short window so the overlay stays kinetic.
 */
const KIND_MATCH = [
  { kind: 'hot_mic',  match: (line) => line.startsWith('🎙'), label: 'CAPTAIN HOT MIC',  Icon: Mic,    tone: 'from-amber-500/90 to-rose-500/90 text-slate-950' },
  { kind: 'champ',    match: (line) => line.startsWith('🏆'), label: 'CHAMPIONSHIP',      Icon: Trophy, tone: 'from-amber-300/95 to-amber-500/95 text-slate-950' },
  { kind: 'rivalry',  match: (line) => line.startsWith('⚔️'), label: 'RIVALRY',           Icon: Swords, tone: 'from-rose-500/95 to-purple-500/95 text-slate-50' },
  { kind: 'momentum', match: (line) => line.startsWith('🔥') && !line.includes('rivalry'), label: 'MOMENTUM', Icon: Flame, tone: 'from-orange-500/95 to-rose-500/95 text-slate-50' },
];

function classifyLine(line) {
  if (!line) return null;
  for (const k of KIND_MATCH) {
    if (k.match(line)) return k;
  }
  return null;
}

export default function ArenaxCommentaryOverlay({ matchId, captainHotMic, latestCommentary }) {
  const [pin, setPin] = useState(null);          // active overlay (hot-mic or composed line)
  const [hotMicState, setHotMicState] = useState(null);

  // Poll for active hot-mic pin once on mount; rely on realtime captainHotMic afterwards.
  useEffect(() => {
    if (!matchId) return;
    api.get(`/arenax/matches/${matchId}/hot-mic`)
      .then(r => {
        if (r.data?.hot_mic) setHotMicState(r.data.hot_mic);
      })
      .catch(() => {});
  }, [matchId]);

  // Realtime hot-mic pin (broadcast 'captain_hot_mic' frames)
  useEffect(() => {
    if (!captainHotMic) return;
    setHotMicState({
      captain_name: captainHotMic.captain_name,
      line: captainHotMic.line,
      at: captainHotMic.at,
      expires_at: captainHotMic.expires_at,
    });
  }, [captainHotMic]);

  // Auto-expire hot-mic
  useEffect(() => {
    if (!hotMicState?.expires_at) return;
    const t = new Date(hotMicState.expires_at).getTime() - Date.now();
    if (t <= 0) {
      setHotMicState(null);
      return;
    }
    const tm = setTimeout(() => setHotMicState(null), Math.min(t, 15000));
    return () => clearTimeout(tm);
  }, [hotMicState]);

  // When hot-mic is present → it wins. Otherwise classify the most recent commentary line.
  useEffect(() => {
    if (hotMicState?.line) {
      setPin({ kind: 'hot_mic', label: 'CAPTAIN HOT MIC', Icon: Mic,
                tone: 'from-amber-500/95 to-rose-500/95 text-slate-950',
                line: hotMicState.line });
      return;
    }
    const line = latestCommentary;
    const cls = classifyLine(line);
    if (cls) {
      setPin({ ...cls, line });
      const tm = setTimeout(() => setPin(null), 8000);  // auto-dismiss
      return () => clearTimeout(tm);
    }
  }, [hotMicState, latestCommentary]);

  if (!pin) return null;
  const Icon = pin.Icon;
  return (
    <div className="sticky top-[64px] z-20 px-4 sm:px-6 mb-3"
      data-testid={`arenax-commentary-overlay-${pin.kind}`}>
      <div className={`rounded-xl px-4 py-3 flex items-start gap-3 shadow-2xl bg-gradient-to-r ${pin.tone}
                          ring-1 ring-white/15 animate-[arenax-overlay-rise_0.5s_ease-out]`}>
        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] tracking-[0.3em] font-black opacity-80 mb-0.5">{pin.label}</div>
          <div className="text-[15px] font-bold leading-tight">{pin.line}</div>
        </div>
      </div>
      <style>{`
        @keyframes arenax-overlay-rise {
          from { transform: translateY(-8px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}


/**
 * Captain Hot Mic input — small "send" composer for captains inside the
 * spectator room. Lightweight per CEO spec — short text snippets only.
 */
export function ArenaxHotMicComposer({ matchId, canPin }) {
  const [snippet, setSnippet] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!snippet.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/arenax/matches/${matchId}/hot-mic`, { snippet: snippet.trim() });
      setSnippet('');
    } catch (_e) {
      // silent
    } finally {
      setSending(false);
    }
  };
  if (!canPin) return null;
  return (
    <div className="mt-3 flex gap-2" data-testid="arenax-hot-mic-composer">
      <div className="relative flex-1">
        <Mic className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
        <input
          value={snippet}
          onChange={e => setSnippet(e.target.value.slice(0, 90))}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          placeholder="Hot mic — short pinned reaction (max 90)"
          data-testid="arenax-hot-mic-input"
          className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-900 border border-amber-500/30
                       text-slate-100 placeholder-slate-500 text-sm focus:outline-none
                       focus:ring-2 focus:ring-amber-500/40"
        />
      </div>
      <button onClick={submit} disabled={!snippet.trim() || sending}
        data-testid="arenax-hot-mic-send"
        className="px-4 py-2 rounded-xl text-xs font-black text-slate-950
                     bg-gradient-to-r from-amber-300 to-rose-400 hover:scale-[1.02]
                     transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
        {sending ? '…' : 'Pin'}
      </button>
    </div>
  );
}
